import {
  createSelector,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';
import { type Location } from '@opentripplanner/types';
import { LngLatBounds } from 'maplibre-gl';
import {
  REALTIME_VEHICLES_TOGGLED_KEY,
  SHOW_EXTRA_MAP_CONTROLS_ON_MOBILE_KEY,
  MAP_LAYER_OPTIONS,
  TRIMET_STREETS_LAYER,
  BUS_LAYER,
  MAX_LAYER,
  WES_LAYER,
  PSC_LAYER,
} from '#/map/constants';
import {
  PARK_AND_RIDE_MODE,
  BASE_LAYER_STORAGE_KEY,
  IS_3D_MODE_STORAGE_KEY,
} from '#/shared/constants';
import { getItem, storeItem } from '#/shared/utils/storage';
import type {
  Company,
  MapHeightMobileRangeChangeData,
  OverlayState,
  SerializableBounds,
  State,
} from '#/map/types';
import type { RootState } from '#/state/store';
import mapConfig from '#/map-config';
import otpUiConfig from '#/otp-ui-config';

const { companies } = otpUiConfig;

const companyLookup: Record<string, Company> = companies.reduce<
  Record<string, Company>
>(
  (obj, company) => ({
    ...obj,
    [company.id]: company,
  }),
  {}
);

const getDefaultOverlayState = (state: State | null = null): OverlayState => ({
  showTripRequestOverlay: false,
  showTripPlanOverlay: false,
  showStopsOverlay: true,
  showStopViewerOverlay: false,
  showHighlightedStopOverlay: false,
  showLocationViewerOverlay: false,
  showVehicleTrackerOverlay: false,
  showRouteGeoJsonOverlay: false,
  showRealtimeVehicleOverlay: state
    ? state.realtimeVehiclesToggled
    : mapConfig.realtimeVehiclesToggled,
});

const initialState: State = {
  hideMapOnMobile: false,
  overlays: getDefaultOverlayState(),
  realtimeVehiclesToggled: getItem(
    REALTIME_VEHICLES_TOGGLED_KEY,
    mapConfig.realtimeVehiclesToggled
  ),
  center: [mapConfig.initLat, mapConfig.initLon],

  // `baseLayer` pertains only to the selected value on the control in
  // map settings, theme and 3d mode also factor into the rendered base layer
  baseLayer:
    // if the layer in local storage exists for the active map load it,
    // otherwise use the default layer
    MAP_LAYER_OPTIONS.map(layer => layer.value).find(
      layerName => layerName === getItem(BASE_LAYER_STORAGE_KEY)
    ) ?? TRIMET_STREETS_LAYER,

  is3DMode: getItem(IS_3D_MODE_STORAGE_KEY, false),
  modes: [],
  companies: [],
  routeLayers: [BUS_LAYER, MAX_LAYER, WES_LAYER, PSC_LAYER],
  scooterStations: [],
  bikeStations: [],
  zipcarLocations: [],
  parkAndRides: [],
  recenterRouteOverlay: true,
  realtimeVehiclesLoading: false,
  dragging: false,

  // set this value to an object with lat & lon values to zoom the map this
  // location via code outside the map container
  zoomToLocation: null,
  zoomToBounds: null,
  zoomLevel: null,
  bearing: null,
  pitch: null,
  isReturningFromStandalonePage: false,

  maxMapOffsetMobile: null,

  // the number of pixels that `window` needs to be scrolled on the y-axis for
  // the mobile map be at its min height and not be covered by the content panel
  mapDefaultScrollMobile: null,
  vehicleRoute: null,
  showExtraMapControlsOnMobile: getItem(
    SHOW_EXTRA_MAP_CONTROLS_ON_MOBILE_KEY,
    false
  ),
};

const mapSlice = createSlice({
  name: 'map',
  initialState,
  reducers: {
    transitTrackerViewOpened(state) {
      state.overlays = {
        ...getDefaultOverlayState(state),
        showHighlightedStopOverlay: true,
        showStopsOverlay: true,
        showStopViewerOverlay: true,
      };
    },
    stopDetailViewOpened(state) {
      return {
        ...state,
        recenterRouteOverlay: false,
        overlays: {
          ...getDefaultOverlayState(state),
          showRouteGeoJsonOverlay: true,
          showStopsOverlay: true,
          showStopViewerOverlay: true,
        },
      };
    },
    locationDetailViewOpened(state) {
      state.overlays = {
        ...getDefaultOverlayState(state),
        showStopsOverlay: true,
        showHighlightedStopOverlay: true,
        showLocationViewerOverlay: true,
      };
    },
    tripPlanViewOpened(state) {
      // When opening the itinerary (trip plan) view, preserve the user's realtime
      // vehicle toggle preference. The default overlay state already sets
      // showRealtimeVehicleOverlay based on `state.realtimeVehiclesToggled`, so we
      // only need to ensure the additional overlays for this view are enabled.
      state.overlays = {
        ...getDefaultOverlayState(state),
        showStopsOverlay: true,
        showTripPlanOverlay: true,
      };
    },
    tripRequestViewOpened(state) {
      return {
        ...state,
        hideMapOnMobile: false,
        overlays: {
          ...getDefaultOverlayState(state),
          showStopsOverlay: true,
          showRealtimeVehicleOverlay: false,
          showTripRequestOverlay: true,
        },
      };
    },
    routeDetailViewOpened(state) {
      return {
        ...state,
        hideMapOnMobile: false,
        recenterRouteOverlay: true,
        overlays: {
          ...getDefaultOverlayState(state),
          showRouteGeoJsonOverlay: true,
          showRealtimeVehicleOverlay: false,
        },
      };
    },
    routeListViewOpened(state) {
      return {
        ...state,
        hideMapOnMobile: true,
        overlays: {
          ...getDefaultOverlayState(state),
          showStopsOverlay: true,
          showStopViewerOverlay: true,
        },
      };
    },
    serviceAlertsViewOpened(state) {
      return {
        ...state,
        hideMapOnMobile: true,
        overlays: {
          ...getDefaultOverlayState(state),
          showStopsOverlay: true,
          showStopViewerOverlay: true,
        },
      };
    },
    // this is action/reducer has a more generalized name because it's
    // used by multiple views
    simpleStopViewOpened(state) {
      return {
        ...state,
        hideMapOnMobile: true,
        overlays: {
          ...getDefaultOverlayState(state),
          showStopsOverlay: true,
          showStopViewerOverlay: true,
          showRealtimeVehicleOverlay: false,
        },
      };
    },
    modeSelectorViewOpened(state) {
      return {
        ...state,
        hideMapOnMobile: true,
        overlays: {
          ...getDefaultOverlayState(state),
          showTripRequestOverlay: true,
          showStopsOverlay: true,
          showRealtimeVehicleOverlay: false,
        },
      };
    },
    vehicleDetailViewOpened(state) {
      state.overlays = {
        ...getDefaultOverlayState(state),
        showStopsOverlay: true,
        showHighlightedStopOverlay: true,
        showStopViewerOverlay: true,
        showVehicleTrackerOverlay: true,
        showRealtimeVehicleOverlay: false,
      };
    },
    viewClosed(state) {
      return {
        ...state,
        hideMapOnMobile: false,
        mapLayersOpen: false,
        overlays: getDefaultOverlayState(state),
      };
    },
    hideMapOnMobile(state) {
      state.hideMapOnMobile = true;
    },
    showMapOnMobile(state) {
      state.hideMapOnMobile = false;
    },
    routeLinkClicked(state) {
      state.recenterRouteOverlay = true;
    },
    baseLayerChanged(state, action: PayloadAction<string>) {
      state.baseLayer = action.payload;
    },
    is3DModeChanged(state, action: PayloadAction<boolean>) {
      state.is3DMode = action.payload;
    },
    rentalCompanyIncluded(state, action: PayloadAction<string>) {
      state.companies.push(action.payload);
      state.modes = [
        // add mode, but ensure the addition isn't a duplicate
        ...new Set(state.modes.concat(companyLookup[action.payload].modes)),
      ];
    },
    rentalCompanyExcluded(state, action: PayloadAction<string>) {
      state.companies = state.companies.filter(c => c !== action.payload);
      state.modes = state.modes.filter(
        m =>
          state.companies.some(c => companyLookup[c].modes === m) ||
          // park & ride is a part of the map `modes` node, but there's
          // no company associated with it and it's toggled via a
          // different reducer so this ensures it's not stripped here
          m === PARK_AND_RIDE_MODE
      );
    },
    routeLayerIncluded(state, action) {
      state.routeLayers.push(action.payload);
    },
    routeLayerExcluded(state, action) {
      state.routeLayers = state.routeLayers.filter(rl => rl !== action.payload);
    },
    parkAndRideToggled(state) {
      state.modes = state.modes.includes(PARK_AND_RIDE_MODE)
        ? state.modes.filter(m => m !== PARK_AND_RIDE_MODE)
        : state.modes.concat(PARK_AND_RIDE_MODE);
    },
    extraMapControlsToggledForMobile(state) {
      const nextShowMapControls = !state.showExtraMapControlsOnMobile;
      state.showExtraMapControlsOnMobile = nextShowMapControls;
      storeItem(SHOW_EXTRA_MAP_CONTROLS_ON_MOBILE_KEY, nextShowMapControls);
    },
    realtimeVehiclesToggled(state) {
      const nextVehiclesToggled = !state.realtimeVehiclesToggled;
      state.realtimeVehiclesToggled = nextVehiclesToggled;
      state.overlays.showRealtimeVehicleOverlay = nextVehiclesToggled;
      storeItem(REALTIME_VEHICLES_TOGGLED_KEY, nextVehiclesToggled);
    },
    setZoomToLocation(state, action: PayloadAction<Location | null>) {
      state.zoomToLocation = action.payload;
    },
    clearZoomToLocation(state) {
      state.zoomToLocation = null;
    },
    setMapCenter(state, action: PayloadAction<number[]>) {
      state.center = action.payload;
    },
    setMapZoomLevel(state, action: PayloadAction<number>) {
      state.zoomLevel = action.payload;
    },
    setMapBearing(state, action: PayloadAction<number>) {
      state.bearing = action.payload;
    },
    setMapPitch(state, action: PayloadAction<number>) {
      state.pitch = action.payload;
    },
    setZoomToBounds(state, action: PayloadAction<SerializableBounds | null>) {
      state.zoomToBounds = action.payload;
    },
    clearZoomToBounds(state) {
      state.zoomToBounds = null;
    },
    setReturningFromStandalonePage(state, action: PayloadAction<boolean>) {
      state.isReturningFromStandalonePage = action.payload;
    },
    clearReturningFromStandalonePage(state) {
      state.isReturningFromStandalonePage = false;
    },
    mapHeightMobileRangeChange(
      state,
      action: PayloadAction<MapHeightMobileRangeChangeData>
    ) {
      const { maxMapOffsetMobile, mapDefaultScrollMobile } = action.payload;

      return {
        ...state,
        maxMapOffsetMobile,
        mapDefaultScrollMobile,
      };
    },
    startDragging(state) {
      state.dragging = true;
    },
    stopDragging(state) {
      state.dragging = false;
    },
  },
});

export const {
  transitTrackerViewOpened,
  stopDetailViewOpened,
  locationDetailViewOpened,
  hideMapOnMobile,
  showMapOnMobile,
  tripPlanViewOpened,
  tripRequestViewOpened,
  routeDetailViewOpened,
  routeListViewOpened,
  serviceAlertsViewOpened,
  simpleStopViewOpened,
  modeSelectorViewOpened,
  vehicleDetailViewOpened,
  viewClosed,
  routeLinkClicked,
  baseLayerChanged,
  is3DModeChanged,
  rentalCompanyIncluded,
  rentalCompanyExcluded,
  routeLayerIncluded,
  routeLayerExcluded,
  parkAndRideToggled,
  realtimeVehiclesToggled,
  setZoomToLocation,
  clearZoomToLocation,
  setMapCenter,
  setMapZoomLevel,
  setMapBearing,
  setMapPitch,
  setZoomToBounds,
  clearZoomToBounds,
  setReturningFromStandalonePage,
  clearReturningFromStandalonePage,
  mapHeightMobileRangeChange,
  extraMapControlsToggledForMobile,
  startDragging,
  stopDragging,
} = mapSlice.actions;

export default mapSlice.reducer;

export const selectShowExtraMapControlsOnMobile = (state: RootState): boolean =>
  state.map.showExtraMapControlsOnMobile;

export const selectIs3DMode = (state: RootState): boolean => state.map.is3DMode;

export const selectHideMapOnMobile = (state: RootState): boolean =>
  state.map.hideMapOnMobile;

export const selectZoomToBounds = createSelector(
  [(state: RootState) => state.map.zoomToBounds],
  (zoomToBounds): LngLatBounds | null =>
    zoomToBounds ? new LngLatBounds(zoomToBounds) : null
);

export const selectOverlays = (state: RootState): OverlayState =>
  state.map.overlays;

export const selectModes = (state: RootState): string[] => state.map.modes;

export const selectRouteLayers = (state: RootState): string[] =>
  state.map.routeLayers;

export const selectCenter = (state: RootState): number[] => state.map.center;

export const selectZoomLevel = (state: RootState): number | null =>
  state.map.zoomLevel;

export const selectBearing = (state: RootState): number | null =>
  state.map.bearing;

export const selectPitch = (state: RootState): number | null => state.map.pitch;

export const selectBaseLayer = (state: RootState): string =>
  state.map.baseLayer;

export const selectCompanies = (state: RootState): string[] =>
  state.map.companies;

export const selectRealtimeVehiclesToggled = (state: RootState): boolean =>
  state.map.realtimeVehiclesToggled;

export const selectIsReturningFromStandalonePage = (
  state: RootState
): boolean => state.map.isReturningFromStandalonePage;
